// LoadDestinationPrompt.jsx — "load this carton where?"
//
// WHY THIS EXISTS
// ---------------
// Every other way into a desk carries its destination with it. The + Add carton
// dialog commits to whichever rail opened it; the Recent chips sit in the Auto
// Imposer's own rail and load there. The Library WORKSPACE is the one entry
// point with no such context: it is a screen of its own, reached from the top
// bar, and "Load →" had one hard-coded answer — the Auto Imposer — with no way
// to say otherwise. An operator who wanted the carton on the editor's desk had
// to load it into a nest they did not want and open the result.
//
// So the Library asks. Three answers, because both is a real one: the Auto
// Imposer and the Editor keep separate desks, and a carton being nested is
// often the same carton being finished by hand.
//
// WHAT THE ANSWER CHANGES
// -----------------------
// Not just which tab opens. The two desks ingest differently and deliberately:
// the Auto Imposer takes the ARTWORK die and runs prepress during the nest,
// while the Editor asks up front whether the carton still needs its flaps
// (EditorFlapPrompt) because nothing downstream of a hand-placed die will. Both
// therefore means the same carton arrives on each desk in the form that desk
// needs, which is why it is one choice here and not two loads.

import React from 'react';
import { useTheme } from './theme';
import DiePreview from './DiePreview';

const OPTIONS = [
  {
    k: 'layout',
    label: 'Auto Imposer',
    hint: 'Nest it with the rest of the job. Prepress runs as part of the layout.',
  },
  {
    k: 'editor',
    label: 'Editor',
    hint: 'Place it by hand. You will be asked whether it still needs its flaps.',
  },
  {
    k: 'both',
    label: 'Both',
    hint: 'One carton on each desk — nest it and finish it by hand.',
  },
];

/**
 * @param {Object|null} pending  { carton } or null when nothing is being loaded
 * @param {(dest: 'layout'|'editor'|'both') => void} onLoad
 * @param {() => void} onCancel
 */
export default function LoadDestinationPrompt({ pending, busy = false, error = null, onLoad, onCancel }) {
  const { P, FN, MO, DISPLAY, fmtLen } = useTheme();
  const [dest, setDest] = React.useState('layout');

  // Back to the default for each carton, so a choice made for one is never
  // silently applied to the next.
  const cartonId = pending?.carton?.id || null;
  React.useEffect(() => { setDest('layout'); }, [cartonId]);

  if (!pending) return null;
  const c = pending.carton;
  const verts = c.geometry?.vertices;
  const bbox = c.geometry?.bbox_mm;

  return (
    <div onClick={busy ? undefined : onCancel} style={{
      position: 'fixed', inset: 0, zIndex: 205, background: 'rgba(20,16,12,.45)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '10vh 16px',
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: '100%', maxWidth: 500, background: P.sheet, border: `0.5px solid ${P.rule}`,
        borderRadius: 14, overflow: 'hidden', boxShadow: '0 24px 60px rgba(20,16,12,.28)',
      }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', padding: '16px 18px 14px' }}>
          {Array.isArray(verts) && verts.length >= 3 && (
            <DiePreview vertices={verts} size={54} />
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: MO, fontSize: 9.5, letterSpacing: 1.5, textTransform: 'uppercase', color: P.accent }}>
              Load carton
            </div>
            <div style={{
              fontFamily: DISPLAY, fontSize: 22, color: P.ink, lineHeight: 1.15, marginTop: 2,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{c.name}</div>
            {Array.isArray(bbox) && bbox.length === 2 && (
              <div style={{ fontFamily: MO, fontSize: 10, color: P.dim, marginTop: 2 }}>
                {fmtLen(bbox[0])} × {fmtLen(bbox[1])}
              </div>
            )}
          </div>
        </div>

        <div style={{ padding: '0 18px 16px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
            {OPTIONS.map((o) => {
              const on = dest === o.k;
              return (
                <button key={o.k} onClick={() => setDest(o.k)} disabled={busy}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 3,
                    textAlign: 'left', width: '100%', padding: '9px 12px', borderRadius: 7,
                    cursor: busy ? 'default' : 'pointer',
                    background: on ? P.accentSoft : P.elev,
                    border: `0.5px solid ${on ? P.accentLine : P.ruleSoft}`,
                  }}>
                  <span style={{ fontFamily: MO, fontSize: 12, color: on ? P.accent : P.ink }}>
                    {on ? '● ' : '○ '}{o.label}
                  </span>
                  <span style={{ fontFamily: FN, fontSize: 10.5, color: P.dim, lineHeight: 1.4, paddingLeft: 15 }}>
                    {o.hint}
                  </span>
                </button>
              );
            })}
          </div>

          {error && (
            <div style={{
              padding: '10px 12px', borderRadius: 6, marginBottom: 14,
              background: P.dangerSoft, border: `0.5px solid ${P.dangerLine}`,
              fontFamily: FN, fontSize: 11.5, color: P.ink, lineHeight: 1.5,
            }}>{error.message || String(error)}</div>
          )}

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={() => onLoad(dest)} disabled={busy} style={{
              fontFamily: MO, fontSize: 11.5, padding: '8px 16px', borderRadius: 6,
              cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1,
              background: P.ink, color: P.sheet, border: `0.5px solid ${P.ink}`,
            }}>{busy ? 'Loading…' : 'Load'}</button>
            <button onClick={onCancel} disabled={busy} style={{
              fontFamily: MO, fontSize: 11.5, padding: '8px 16px', borderRadius: 6,
              cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1,
              background: 'transparent', color: P.muted, border: `0.5px solid ${P.rule}`,
            }}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}
